/**
 * `/why-ryerson/ryerson-commitment/ryerson-gives-back/`: the Ryerson Gives Back page. On any other
 * `NEXT_PUBLIC_COMPANY` the page isn't built. SEO fields, the By the Numbers list and the
 * site-search entry come from
 * `src/content/ryerson/why-ryerson/ryerson-commitment/ryerson-gives-back.ts`.
 * The hero and the other sections keep their copy beside their components in
 * `components/WhyRyerson/RyersonCommitment/RyersonGivesBackPage/`.
 *
 * Under the hero (`RyersonGivesBackHeader`), one `AllSections` list: By the Numbers
 * (`components/WhyRyerson/RyersonCommitment/SubPage/FileExplorer.tsx`), Unique Impact, A Special
 * Opportunity, Volunteering, College Scholarship Program, and Contact Us. Each section title comes
 * from that section's copy, so editing a title changes the section's anchor id. Contact Us is
 * marked `hide`: below `lg` it is left out of the accordion and the page renders the one-button
 * `ContactUsRefactor` itself.
 *
 * Linked, in this repository, from the landing page's teaser
 * (`components/WhyRyerson/RyersonCommitment/RyersonGivesBack/Content.tsx`), the Why Ryerson card
 * grid (`components/Shared/RyersonCommitment/Content.tsx`),
 * `components/Shared/InOurDNA/Content.tsx` and the careers Training and Development copy
 * (`src/content/ryerson/about-us/careers/working-at-ryerson/training-and-development/index.ts`).
 */
import React from "react";
import { AllSections, Sections } from "@components/Shared/AllSections/AllSections";
import RyersonGivesBackHeader from "@components/WhyRyerson/RyersonCommitment/RyersonGivesBackPage/RyersonGivesBackHeader/RyersonGivesBackHeader";
import UniqueImpact from "@components/WhyRyerson/RyersonCommitment/RyersonGivesBackPage/UniqueImpact/UniqueImpact";
import Volunteering from "@components/WhyRyerson/RyersonCommitment/RyersonGivesBackPage/Volunteering/Volunteering";
import CollegeScholarshipProgramme from "@components/WhyRyerson/RyersonCommitment/RyersonGivesBackPage/CollegeScholarshipProgramme/CollegeScholarshipProgramme";
import SpecialOpportunity from "@components/WhyRyerson/RyersonCommitment/RyersonGivesBackPage/SpecialOpportunity/SpecialOpportunity";
import { useApplication } from "@ryerson/frontend.application";
import { UniqueImpactContent } from "@components/WhyRyerson/RyersonCommitment/RyersonGivesBackPage/UniqueImpact/UniqueImpactContent";
import { VolunteeringContent } from "@components/WhyRyerson/RyersonCommitment/RyersonGivesBackPage/Volunteering/VolunteeringContent";
import { CollegeScholarshipProgrammeContent } from "@components/WhyRyerson/RyersonCommitment/RyersonGivesBackPage/CollegeScholarshipProgramme/CollegeScholarshipProgrammeContent";
import { SpecialOpportunityContent } from "@components/WhyRyerson/RyersonCommitment/RyersonGivesBackPage/SpecialOpportunity/SpecialOpportunityContent";
import ContactUsRefactor from "@components/Shared/ContactUsRefactor/ContactUs";
import { DefaultContactUsContent } from "@components/Shared/ContactUsRefactor/Content";
import { Media } from "@ryerson/frontend.next";
import FileExplorer from "@components/WhyRyerson/RyersonCommitment/SubPage/FileExplorer";
import MetaHead from "@components/Shared/model/MetaHead";
import { MetaObject } from "@content/ryerson/why-ryerson/ryerson-commitment/ryerson-gives-back";
import { ByTheNumbersContent } from "@content/ryerson/why-ryerson/ryerson-commitment/ryerson-gives-back";
import { allContentfulHeaderCalls, HeaderPageProps } from "@ryerson/frontend.contentful";
import { getHeaderData } from "@components/NextLayouts/nextUtilities";
import HeaderLayout from "@components/NextLayouts/HeaderLayout";

/** Ryerson build only. Fetches the header and footer data from Contentful at build time. */
export const getStaticProps = async () => {
	if (process.env.NEXT_PUBLIC_COMPANY !== "ryerson") {
		return { notFound: true }; // tells Next.js to skip page generation and return 404
	}

	return await allContentfulHeaderCalls();
};

/**
 * Renders the page in the current language, falling back to English for each section's copy; see
 * the file header for the layout.
 */
const Page: React.FC<HeaderPageProps> = (props) => {
	const {
		localization: { language },
	} = useApplication();

	const uniqueImpactContent = UniqueImpactContent[language] || UniqueImpactContent.en;
	const byTheNumbersContent = ByTheNumbersContent[language] || ByTheNumbersContent.en;
	const volunteeringContent = VolunteeringContent[language] || VolunteeringContent.en;
	const collegeScholarshipProgramContent =
		CollegeScholarshipProgrammeContent[language] || CollegeScholarshipProgrammeContent.en;
	const specialOpportunityContent =
		SpecialOpportunityContent[language] || SpecialOpportunityContent.en;

	const sections: Sections[] = [
		{
			title: byTheNumbersContent.title,
			cmp: <FileExplorer content={byTheNumbersContent} />,
		},
		{ title: uniqueImpactContent.title, cmp: <UniqueImpact /> },
		{ title: specialOpportunityContent[0].title, cmp: <SpecialOpportunity /> },
		{ title: volunteeringContent.title, cmp: <Volunteering /> },
		{ title: collegeScholarshipProgramContent[0].title, cmp: <CollegeScholarshipProgramme /> },
		{
			title: DefaultContactUsContent[language].title,
			cmp: <ContactUsRefactor oneButtonVariant={false} />,
			hide: true,
		},
	];

	return (
		<HeaderLayout {...getHeaderData(props)}>
			<MetaHead content={MetaObject[language]} />
			<RyersonGivesBackHeader />
			<AllSections sections={sections} />
			<Media lessThan="lg">
				<ContactUsRefactor
					oneButtonVariant={true}
					withImage={false}
					background="secondary"
				/>
			</Media>
		</HeaderLayout>
	);
};

export default Page;
